import { FC, SVGProps } from 'react';
import classNames from 'classnames';
import { Tooltip } from './Tooltip';
import { useT } from '../translation/src';

type IconComponent = FC<SVGProps<SVGSVGElement>> | React.ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;

interface ToolButtonProps {
  icon: IconComponent;
  name: string;
  active?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  onClick: () => void;
}

/**
 * ToolButton - Button for individual tools within a persona
 *
 * Displays a tool icon with label and active state.
 * Used in the toolbar to select tools like point measurement, distance, etc.
 */
export const ToolButton: FC<ToolButtonProps> = ({ icon: Icon, name, active, disabled, disabledTooltip, onClick }) => {
  // Get translated name for tooltip and aria-label
  const translatedName = useT(name, { swc: true });
  const disabledTooltipText = useT(disabledTooltip || '', { swc: true });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick();
    // Remove focus to prevent white border after clicking
    e.currentTarget.blur();
  };

  const button = (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={classNames(
        'flex items-center justify-center w-10 h-10 rounded-lg transition-all',
        'hover:bg-gray-200 dark:hover:bg-gray-700',
        'focus:outline-none', // Remove default focus outline
        {
          'bg-neon-green-300 text-black': active,
          'hover:bg-gray-300 dark:hover:bg-gray-700': !active && !disabled,
          'opacity-50 cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
        },
      )}
      aria-label={translatedName}
    >
      <Icon className="w-7 h-7 flex-shrink-0" />
    </button>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip message={<span>{disabledTooltipText}</span>} delay={0} placement="bottom" className="bg-warning-yellow-200 border-warning-yellow-500 text-warning-yellow-700 dark:bg-[#313131] dark:text-warning-yellow-500">
        {button}
      </Tooltip>
    );
  }

  if (!disabled) {
    return (
      <Tooltip message={<span>{translatedName}</span>} delay={0}>
        {button}
      </Tooltip>
    );
  }

  return button;
};
